import React, {useMemo, useState} from 'react';
import {FormattedHTMLMessage, useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import styled from 'styled-components';
import {FlexColumn, FlexRow, Modal, SearchField, Table, Text} from '@forecast-it/design-system';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import Util, {MAX_FILES_PER_UPLOAD} from '../../forecast-app/shared/util/util';
import AttachProjectFileToTaskMutation from '../../mutations/attach_project_file_to_task_mutation';
// Same helper Util.uploadFiles uses for its partial-failure summary, so a partial attach failure
// reads the way a partial upload failure already does.
import {createToast} from '../../forecast-app/shared/components/toasts/another-toast/toaster';

type FolderRef = {id: string} | null;
type ProjectFile = {id: string; name: string; size: number; mimeType: string; folder: FolderRef};
type ProjectFolder = {id: string; name: string; folder: FolderRef};
type TaskFile = {id: string};

// The attach mutation reports a business-rule rejection in its payload rather than throwing, so
// onSuccess has to inspect it. Only the two fields this flow reads are declared.
type AttachMutationResponse = {
	attachProjectFileToTask?: {error?: string | null; filename?: string | null} | null;
} | null;

type AttachProjectFilesRelayProps = {
	viewer?: {
		project?: {
			files: {edges: {node: ProjectFile}[]};
			folders: {edges: {node: ProjectFolder}[]};
		} | null;
		task?: {
			referencedFiles: {edges: {node: TaskFile}[]};
		} | null;
	} | null;
} | null;

export type PickerRow = {
	id: string;
	name: string;
	isFolder: boolean;
	alreadyAttached: boolean;
	size?: number;
	mimeType?: string;
};

// The design-system Text takes no onClick -- TextProps has none -- so the click target is a real
// button reset to look like inline text. That is also the accessible choice for a navigation
// control: it is focusable and keyboard-operable, which a clickable span would not be.
const BreadcrumbCrumb = styled.button`
	background: none;
	border: none;
	padding: 0;
	margin: 0;
	font: inherit;
	color: inherit;
	cursor: pointer;
`;

const folderIdOf = (item: {folder: FolderRef}): string | null => (item.folder ? item.folder.id : null);

/**
 * Rows for one level of the folder tree. Mirrors ProjectFilesPage.setElements: search is applied
 * first, then the level filter, so a search never reaches into another folder. Folders are returned
 * as rows so the user can descend, but always carry isFolder so nothing can select them — a folder
 * cannot be attached to a task.
 */
export function pickerRowsForLevel(
	projectFiles: ProjectFile[],
	projectFolders: ProjectFolder[],
	openFolderId: string | null,
	searchValue: string,
	taskFiles: TaskFile[]
): PickerRow[] {
	const attachedIds = new Set(taskFiles.map(f => f.id));
	const matches = (name: string) => !searchValue || name.toLowerCase().includes(searchValue.toLowerCase());

	const folderRows: PickerRow[] = projectFolders
		.filter(folder => folderIdOf(folder) === openFolderId && matches(folder.name))
		.map(folder => ({id: folder.id, name: folder.name, isFolder: true, alreadyAttached: false}));

	const fileRows: PickerRow[] = projectFiles
		.filter(file => folderIdOf(file) === openFolderId && matches(file.name))
		.map(file => ({
			id: file.id,
			name: file.name,
			isFolder: false,
			// Matched on file id: a link points at the project's own file row, so the id is the
			// identity on both sides. There is no second row and no shared key to compare.
			alreadyAttached: attachedIds.has(file.id),
			size: file.size,
			mimeType: file.mimeType,
		}));

	return [...folderRows, ...fileRows];
}

/**
 * The footer button's only guard against firing one mutation per selected file without bound.
 * Exported so the boundary is assertable directly — reading the constant back in a test proves
 * nothing about whether this rule is applied.
 */
export function isAttachBlocked(selectedCount: number): boolean {
	return selectedCount === 0 || selectedCount > MAX_FILES_PER_UPLOAD;
}

/**
 * Mirrors the STRUCTURE of the summary Util.uploadFiles builds, with its own message ids.
 *
 * Deliberately does NOT reuse file_upload.uploaded-file / file_upload.rejected-files. They read
 * "The following files were uploaded:" and "The following files are not allowed to be uploaded:".
 * Nothing is uploaded in this flow — that is the entire point of the feature — so the success toast
 * would be actively misleading and the failure toast wrong about the cause. The structural
 * mirroring is right; the words are not.
 */
export function buildAttachSummary(attached: string[], failed: string[]) {
	return (
		<div>
			{attached.length > 0 ? <FormattedHTMLMessage id="task_modal.attach_project_file_attached" /> : null}
			{attached.length > 0 ? <br /> : null}
			{attached.length > 0 ? attached.join(', ') : null}
			{attached.length > 0 ? <br /> : null}
			{failed.length > 0 ? <FormattedHTMLMessage id="task_modal.attach_project_file_failed" /> : null}
			{failed.length > 0 ? <br /> : null}
			{failed.length > 0 ? failed.join(', ') : null}
		</div>
	);
}

export const AttachProjectFilesModalContent = ({
	projectFiles,
	projectFolders,
	taskFiles,
	taskId,
	closeModal,
}: {
	projectFiles: ProjectFile[];
	projectFolders: ProjectFolder[];
	taskFiles: TaskFile[];
	taskId: string;
	closeModal: () => void;
}) => {
	const {formatMessage} = useIntl();
	const [searchValue, setSearchValue] = useState('');
	// The trail of folders the user has descended through. Its last element is the open folder; empty
	// means the project's top level. Doubles as the breadcrumb, so there is one source of truth for
	// "where am I" — ProjectFilesPage keeps these separate (selectedFolder plus pathFolders) and has
	// to reconcile them.
	const [trail, setTrail] = useState<ProjectFolder[]>([]);
	// Selection is keyed by file id and lives above the level state, so descending into a folder
	// keeps what is already ticked and the cap applies to the whole selection, not per folder.
	const [selectedById, setSelectedById] = useState<Map<string, PickerRow>>(new Map());

	const openFolderId = trail.length ? trail[trail.length - 1].id : null;

	const rows = useMemo(
		() => pickerRowsForLevel(projectFiles, projectFolders, openFolderId, searchValue, taskFiles),
		[projectFiles, projectFolders, openFolderId, searchValue, taskFiles]
	);

	const selectedRows = Array.from(selectedById.values());
	const attachBlocked = isAttachBlocked(selectedRows.length);

	const openFolder = (row: PickerRow) => {
		const folder = projectFolders.find(f => f.id === row.id);
		if (folder) {
			setTrail([...trail, folder]);
			setSearchValue('');
		}
	};

	// index -1 returns to the project's top level.
	const goToTrailIndex = (index: number) => {
		setTrail(trail.slice(0, index + 1));
		setSearchValue('');
	};

	const onSelectionChange = (levelSelection: PickerRow[]) => {
		// Table reports the selection for the rows it currently has, so merging by id is what
		// preserves ticks made in other folders. Rows at this level that are no longer ticked are
		// dropped; rows from elsewhere are left alone.
		const next = new Map(selectedById);
		rows.filter(r => !r.isFolder).forEach(r => next.delete(r.id));
		(levelSelection || []).forEach(row => next.set(row.id, row));
		setSelectedById(next);
	};

	const attachSelected = () => {
		// Util.CommitMutation supplies the Relay environment and the retry wrapper; call it with the
		// mutation module rather than invoking commit() directly, matching every other call site. Its
		// signature is (mutationClass, input, onSuccess, retry, uploadables, onError), so `true` and
		// `null` must be passed positionally to reach onError.
		//
		// Both callbacks are load-bearing. The mutation reports a business-rule rejection as
		// payload.error rather than throwing, so with no onSuccess a source file deleted between
		// render and click fails SILENTLY: the modal closes and the user believes it worked.
		const attached: string[] = [];
		const failed: string[] = [];
		let settled = 0;
		const total = selectedRows.length;

		// One summary toast once every per-file mutation has settled, mirroring the pending-counter
		// pattern in Util.uploadFiles.
		const settle = () => {
			settled += 1;
			if (settled < total) return;
			createToast({duration: 5000, message: buildAttachSummary(attached, failed)});
		};

		selectedRows.forEach(row => {
			Util.CommitMutation(
				AttachProjectFileToTaskMutation,
				{taskId, sourceFileId: row.id},
				(response: AttachMutationResponse) => {
					// The payload carries the rejection rather than throwing.
					const payload = response && response.attachProjectFileToTask;
					if (payload && payload.error) {
						failed.push(payload.filename || row.name);
					} else {
						attached.push(row.name);
					}
					settle();
				},
				true,
				null,
				() => {
					failed.push(row.name);
					settle();
				}
			);
		});
		closeModal();
	};

	const hasAnything = projectFiles.length > 0 || projectFolders.length > 0;

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal}>
			<Modal.Title>{formatMessage({id: 'task_modal.attach_project_file'})}</Modal.Title>
			<Modal.Content>
				{!hasAnything ? (
					<div data-testid={'attach-project-files-empty'} data-cy={'attach-project-files-empty'}>
						{formatMessage({id: 'task_modal.attach_project_file_empty'})}
					</div>
				) : (
					<FlexColumn gap={'m'}>
						<FlexRow gap={'s'} data-cy={'attach-project-files-breadcrumb'}>
							<BreadcrumbCrumb type={'button'} onClick={() => goToTrailIndex(-1)}>
								<Text>{formatMessage({id: 'project_files.all_files'})}</Text>
							</BreadcrumbCrumb>
							{trail.map((folder, index) => (
								<BreadcrumbCrumb key={folder.id} type={'button'} onClick={() => goToTrailIndex(index)}>
									<Text>{`/ ${folder.name}`}</Text>
								</BreadcrumbCrumb>
							))}
						</FlexRow>
						<SearchField
							isExpandable={false}
							value={searchValue}
							onChange={setSearchValue}
							placeholder={formatMessage({id: 'common.search'})}
						/>
						<Table
							data={rows}
							onSelectionChange={onSelectionChange}
							// Folders are listed for navigation but can never be attached, and a file
							// already linked to this task cannot be selected again. This prop receives
							// the WRAPPED row, unlike onSelectionChange and the column's onClick.
							enableRowSelection={(row: {original: PickerRow}) =>
								!row.original.isFolder && !row.original.alreadyAttached
							}
							width={'100%'}
						>
							<Table.TextColumn
								header={formatMessage({id: 'common.name'})}
								accessorKey={'name'}
								// Higher-order by design: onClick is asked for a handler per row and
								// must RETURN one, not call it. Returning openFolder(row) directly
								// would descend during render.
								onClick={(row: PickerRow) => (row.isFolder ? () => openFolder(row) : undefined)}
							/>
						</Table>
					</FlexColumn>
				)}
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
				<Modal.PrimaryFooterButton onPress={attachSelected} disabled={attachBlocked}>
					{formatMessage({id: 'task_modal.attach_project_file_confirm'}, {count: selectedRows.length})}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

// Both ids are Relay global ids and both arguments run them through fromGlobalId server-side:
// viewer.project takes `internalId`, but viewer.task has no internalId argument at all -- its
// equivalent is plain `id`.
export const attachProjectFilesModalQuery = graphql`
	query AttachProjectFilesModalQuery($projectId: ID, $taskId: ID) {
		viewer {
			actualPersonId
			component(name: "attach_project_files_modal")
			project(internalId: $projectId) {
				id
				files(first: 10000) @connection(key: "Project_files", filters: []) {
					edges {
						node {
							id
							name
							mimeType
							size
							folder {
								id
							}
						}
					}
				}
				# Drives the hierarchy. Folder.folder is the parent, so one flat list is enough to
				# reconstruct every level.
				folders(first: 10000) @connection(key: "Project_folders", filters: []) {
					edges {
						node {
							id
							name
							folder {
								id
							}
						}
					}
				}
			}
			task(id: $taskId) {
				id
				# Drives the already-attached rule. Nothing else produces it: showModal passes only
				# ids, and taskModalV3_viewer queries neither side. Without it the prop is undefined
				# and -- if defaulted to [] to dodge the crash -- every already-linked file becomes
				# selectable again, silently defeating the acceptance criterion.
				referencedFiles(first: 10000) @connection(key: "Task_referencedFiles", filters: []) {
					edges {
						node {
							id
						}
					}
				}
			}
		}
	}
`;

/**
 * The Task_referencedFiles key matches the one the attach mutation's RANGE_ADD appends to, so an
 * attach made from this modal updates its own view of what is already linked without a refetch.
 */
const AttachProjectFilesModal = ({
	taskId,
	projectId,
	closeModal,
}: {
	taskId: string;
	projectId: string;
	closeModal: () => void;
}) => (
	<ForecastQueryRenderer
		key="query-render-attachProjectFilesModal"
		query={attachProjectFilesModalQuery}
		modal={true}
		showLoader={false}
		variables={{projectId, taskId}}
		render={(relayProps: AttachProjectFilesRelayProps) => {
			const project = relayProps?.viewer?.project;
			const task = relayProps?.viewer?.task;
			if (!project || !task) return null;
			return (
				<AttachProjectFilesModalContent
					projectFiles={project.files.edges.map(edge => edge.node)}
					projectFolders={project.folders.edges.map(edge => edge.node)}
					taskFiles={task.referencedFiles.edges.map(edge => edge.node)}
					taskId={taskId}
					closeModal={closeModal}
				/>
			);
		}}
	/>
);

export default AttachProjectFilesModal;
